import css from 'styled-jsx/css';
import { screen, colors } from 'components/styles/variables';
import classNames from 'classnames';
import Header from './Header';
import Actions from './Actions';
import SellerDescription from './SellerDescription';
import Spacer from '../../Shared/Spacer';
import LocationDisplay from './LocationDisplay';

interface Props {
  dealerName: string;
  mapImageUrl: string;
  zip: string;
  address: string;
  city: string;
  state: string;
  phoneNumber: string | null;
  openLeadForm: () => void;
  mapUrl: string;
  website: string | null;
  description: string | null;
  isRegional: boolean;
  transferAvailable: boolean;
  experienceBody?: string;
  make: string;
  model: string;
  year: number;
  trim: string | null;
  vin: string;
  dealerDisclaimer?: string | null;
}

const headerCss = css.resolve`
  @media screen and (min-width: ${screen.smMin}) {
    .header {
      order: 1;
      flex-basis: 100%;
      margin-bottom: 24px;
    }
  }
`;

const actionsCss = css.resolve`
  @media screen and (min-width: ${screen.smMin}) {
    .actions {
      order: 3;
      margin-left: 36px;
      align-self: center;
    }
  }
`;

const descriptionCss = css.resolve`
  @media screen and (min-width: ${screen.smMin}) {
    .description {
      order: 4;
    }
  }
`;

const mapCss = css.resolve`
  @media screen and (min-width: ${screen.smMin}) {
    .map {
      order: 2;
    }
  }
`;

const experienceCss = css.resolve`
  .experience-body {
    background-color: ${colors.G1};
    border-radius: 8px;
    border: 1px solid ${colors.G2};
    padding: 14px 16px 16px 16px;
    color: ${colors.BLACK};
    margin-bottom: 0px;
  }
  @media screen and (min-width: ${screen.smMin}) {
    .experience-body {
      order: 3;
      border-radius: 16px;
      flex-basis: 100%;
      margin-top: 33px;
      padding: 18px 20px;
      margin-bottom: 14px;
    }
    .header {
      order: 1;
      flex-basis: 100%;
      margin-bottom: 24px;
    }
  }
  .seller-profile {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
  }
  @media screen and (min-width: ${screen.smMin}) {
    .seller-profile {
      flex-direction: row;
    }
    .actions {
      order: 3;
      margin-left: 36px;
      align-self: center;
    }
  }
`;

function Break(): JSX.Element {
  return (
    <div className="break">
      <style jsx>{`
        .break {
          flex-basis: 100%;
          height: 0;
        }
      `}</style>
    </div>
  );
}

export default function SellerProfile({
  dealerName,
  mapImageUrl,
  phoneNumber,
  openLeadForm,
  website,
  address,
  city,
  state,
  zip,
  mapUrl,
  description,
  isRegional,
  transferAvailable,
  experienceBody,
  year,
  make,
  model,
  trim,
  vin,
  dealerDisclaimer,
}: Props): JSX.Element {
  return (
    <section className={`seller-profile ${experienceCss.className}`}>
      <LocationDisplay
        className={classNames('map', mapCss.className)}
        imageUrl={mapImageUrl}
        mapUrl={mapUrl}
        zip={zip}
        city={city}
        state={state}
        address={address}
        transferAvailable={transferAvailable}
        isRegional={isRegional}
      />
      <Spacer size={'16px'} />
      <Header dealerName={dealerName} className={`header ${headerCss.className}`} />
      <Break />
      <Spacer size={'16px'} smSize={'24px'} />
      <Actions
        className={`actions ${actionsCss.className}`}
        phoneNumber={phoneNumber}
        openLeadForm={openLeadForm}
        website={website}
        year={year}
        make={make}
        model={model}
        trim={trim}
        vin={vin}
      />
      <Break />
      {experienceBody && (
        <p className={`experience-body ${experienceCss.className}`}>{experienceBody}</p>
      )}
      <SellerDescription
        description={description}
        className={`description ${descriptionCss.className}`}
        dealerDisclaimer={dealerDisclaimer}
      />
      {mapCss.styles}
      {descriptionCss.styles}
      {actionsCss.styles}
      {headerCss.styles}
      {experienceCss.styles}
    </section>
  );
}
